import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import FormDrawer from '../form-drawer/form-drawer.component';
import * as yup from 'yup';
import IncTextField from 'components/forms/fields/inc-text-field/inc-text-field.component';
import { ButtonProps } from '@mui/material';

type DeleteBlocker = {
  disable: boolean;
  loading: boolean;
};

type ConfirmationDrawerType = {
  open: boolean;
  close: () => void;
  onValid: () => void;
  color: ButtonProps['color'];
  validInputText?: string;
  validInputTextPlaceholder?: string;
  children?: any;
  subtitle?: string;
  submitButtonLabel?: string;
  cancel?: () => void;
  deleteBlocker?: DeleteBlocker;
};

/**
 *
 * @param {string} color - changes the button color on the FormDrawer
 * @param {string} validInputText  indicates we want an input on the form that must match this string to confirm
 */
const ConfirmationDrawer = ({
  close,
  onValid,
  color,
  validInputText,
  validInputTextPlaceholder,
  children,
  deleteBlocker,
  ...rest
}: ConfirmationDrawerType) => {
  const { t } = useTranslation();
  const formMethods = useForm(
    validInputText
      ? {
          mode: 'onChange',
          resolver: yupResolver(
            yup.object().shape({
              validInputText: yup
                .string()
                .required()
                .matches(
                  new RegExp(`^${validInputText}$`, 'i'),
                  t('drawer.confirmationDrawer.validInputTextError', {
                    validInputText,
                  }),
                )
                .default(''),
            }),
          ),
        }
      : {},
  );

  const onClose = () => {
    formMethods.reset();
    formMethods.clearErrors();
    close();
  };

  const handleValid = async () => {
    await onValid();
    formMethods.reset();
    formMethods.clearErrors();
    close();
  };

  const { isSubmitting, isValid, isValidating } = formMethods.formState;

  return (
    <FormDrawer
      title={t('drawer.confirmationDrawer.title')}
      onValid={handleValid}
      onClose={onClose}
      cancel={onClose}
      formMethods={formMethods}
      submitButtonProps={{
        color,
        disabled: !isValid || deleteBlocker?.disable,
        loading: isSubmitting || isValidating,
      }}
      isLoading={deleteBlocker?.loading}
      {...rest}
      data-testid="ConfirmationDrawer.Drawer"
    >
      {children}
      {validInputText && (
        <IncTextField
          name="validInputText"
          placeholder={validInputTextPlaceholder}
          data-testid="ValidInputTextField"
          autoComplete="off" // Turn off autofill
        />
      )}
    </FormDrawer>
  );
};

export default ConfirmationDrawer;
